import { useCallback, useState } from 'react';
import { ApolloClient } from 'apollo-client';
import {
    DELETE_USER_GUIDE_SCOPE_GROUP,
    SAVE_USER_GUIDE_SCOPE_GROUP,
} from '../../../../../gql/userGuideScopes';
import { UserGuideScope } from '../../../../../shared/types';
import { MessageTypes } from '../../../../providers/NotificationProvider';
import { extractErrorMessage, getMutationError } from '../utils/errorUtils';

type SaveScopeGroupData = {
    saveUserGuideScopeGroup?: UserGuideScope[];
};

type DeleteScopeGroupData = {
    deleteUserGuideScopeGroup?: boolean;
};

interface UserGuideScopeFormLike {
    isValid: boolean;
    setDirty: () => void;
    name: {
        value: string;
    };
    scope: {
        value: string[];
    };
}

interface UseUserGuideScopeActionsParams {
    apolloClient: ApolloClient<object>;
    form: UserGuideScopeFormLike;
    isFormValid: boolean;
    isNew: boolean;
    editGroupItems: UserGuideScope[];
    document?: UserGuideScope;
    onCancel: () => void;
    showMessage: (message: string, type: MessageTypes) => void;
    showErrorBanner: (message: string) => void;
}

interface PreventableSubmitEvent {
    preventDefault: () => void;
}

const useUserGuideScopeActions = ({
    apolloClient,
    form,
    isFormValid,
    isNew,
    editGroupItems,
    document,
    onCancel,
    showMessage,
    showErrorBanner,
}: UseUserGuideScopeActionsParams) => {
    const [localBusy, setLocalBusy] = useState(false);

    const getOriginalItems = useCallback(
        () =>
            editGroupItems.length ? editGroupItems : document ? [document] : [],
        [document, editGroupItems]
    );

    const saveScopes = useCallback(
        async (ev: PreventableSubmitEvent) => {
            ev.preventDefault();
            form.setDirty();

            if (!isFormValid || !form.isValid) {
                return;
            }

            const name = (form.name.value || '').trim();
            const selectedScopes = (form.scope.value || [])
                .map((scope) => (scope || '').trim())
                .filter(Boolean);

            if (!name || selectedScopes.length === 0) {
                return;
            }

            try {
                setLocalBusy(true);
                const originalItems = getOriginalItems();
                const result = await apolloClient.mutate<SaveScopeGroupData>({
                    mutation: SAVE_USER_GUIDE_SCOPE_GROUP,
                    variables: {
                        item: {
                            name,
                            scopes: selectedScopes,
                            originalIds: originalItems.map((item) => item._id),
                        },
                    },
                    errorPolicy: 'all',
                });
                const mutationError = getMutationError(result);
                if (mutationError) {
                    throw new Error(mutationError);
                }
                if (!result.data?.saveUserGuideScopeGroup?.length) {
                    throw new Error('The scope operation returned no result.');
                }

                showMessage(
                    isNew
                        ? `Created ${selectedScopes.length} user guide scope mapping${
                              selectedScopes.length === 1 ? '' : 's'
                          }.`
                        : 'Updated user guide scope mappings.',
                    MessageTypes.success
                );
                onCancel();
            } catch (error) {
                const errorMessage = extractErrorMessage(
                    error,
                    'Unable to save the scope. Please try again.'
                );
                showErrorBanner(errorMessage);
            } finally {
                setLocalBusy(false);
            }
        },
        [
            apolloClient,
            form,
            getOriginalItems,
            isFormValid,
            isNew,
            onCancel,
            showErrorBanner,
            showMessage,
        ]
    );

    const deleteScopeGroup = useCallback(async () => {
        const originalItems = getOriginalItems();

        try {
            setLocalBusy(true);
            const result = await apolloClient.mutate<DeleteScopeGroupData>({
                mutation: DELETE_USER_GUIDE_SCOPE_GROUP,
                variables: { ids: originalItems.map((item) => item._id) },
                errorPolicy: 'all',
            });
            const mutationError = getMutationError(result);
            if (mutationError) {
                throw new Error(mutationError);
            }
            if (result.data?.deleteUserGuideScopeGroup !== true) {
                throw new Error('The scope operation returned no result.');
            }

            showMessage(
                'Deleted user guide scope mappings.',
                MessageTypes.success
            );
            onCancel();
        } catch (error) {
            const errorMessage = extractErrorMessage(
                error,
                'Unable to delete the scope. Please try again.'
            );
            showErrorBanner(errorMessage);
        } finally {
            setLocalBusy(false);
        }
    }, [
        apolloClient,
        getOriginalItems,
        onCancel,
        showErrorBanner,
        showMessage,
    ]);

    return {
        localBusy,
        saveScopes,
        deleteScopeGroup,
    };
};

export default useUserGuideScopeActions;
